import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Award = { id: string; state: string | null; program_round: string | null; grant_program: string; award_status: string; prime_contractor: string | null; award_amount_usd: number | null; project_count: number; };
const STATUS = ['active','defaulted','relinquished','sold'];
const SC: Record<string,string> = { active:'#065f46', defaulted:'#991b1b', relinquished:'#92400e', sold:'#374151' };

export default function GrantAwardsPage() {
  const qc = useQueryClient();
  const { data: awards = [], isLoading } = useQuery<Award[]>({ queryKey:['bead-awards'], queryFn:()=>api.get('/bead/awards') });
  const { data: ga } = useQuery<{available_programs:string[]}>({ queryKey:['grant-assignments-progs'], queryFn:()=>api.get('/bead/grant-assignments') });
  const progs = ga?.available_programs ?? ['BEAD','RDOF','ReConnect','CPF'];
  const [busy,setBusy] = useState<string|null>(null);
  const [msg,setMsg] = useState<string|null>(null);
  const upd = async (id:string, body:any) => {
    setBusy(id);
    try { await api.put('/bead/awards/'+id+'/status', body); qc.invalidateQueries({queryKey:['bead-awards']}); setMsg('Saved.'); }
    catch(e:any){ setMsg('Error: '+(e?.message??e)); }
    finally { setBusy(null); }
  };
  if (isLoading) return <div className="card"><div className="card__body">Loading...</div></div>;
  return (
    <div style={{maxWidth:1100}}>
      <div className="page__header"><div>
        <h1 className="page__title">Compliance - Grant Awards</h1>
        <div className="page__subtitle">Your company's grant awards and their status. A <b>defaulted</b> or <b>relinquished</b> award stops being an enforceable commitment - its locations free back up for other programs (reason-code-4). Status here drives the "you already won here" check on the Builder.</div>
      </div></div>
      {msg && <div style={{background:'#ecfdf5',border:'1px solid #6ee7b7',color:'#065f46',borderRadius:6,padding:'8px 12px',marginBottom:12,fontSize:13}}>{msg}</div>}
      <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:6,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead><tr style={{background:'#f8fafc',borderBottom:'1px solid #e2e8f0'}}>
            <th style={{textAlign:'left',padding:'8px 12px',fontWeight:600,color:'#475569'}}>Award</th>
            <th style={{textAlign:'left',padding:'8px 12px',fontWeight:600,color:'#475569',width:170}}>Program</th>
            <th style={{textAlign:'left',padding:'8px 12px',fontWeight:600,color:'#475569',width:170}}>Status</th>
            <th style={{textAlign:'right',padding:'8px 12px',fontWeight:600,color:'#475569',width:90}}>Projects</th>
          </tr></thead>
          <tbody>
            {awards.map(a=>(
              <tr key={a.id} style={{borderTop:'1px solid #f1f5f9',opacity:busy===a.id?0.5:1}}>
                <td style={{padding:'8px 12px'}}>
                  <div style={{fontWeight:600}}>{a.prime_contractor || 'Award'}{a.state?(' - '+a.state):''}</div>
                  <div style={{fontSize:11,color:'#94a3b8'}}>{a.program_round||''}{a.award_amount_usd?(' - $'+(Number(a.award_amount_usd)/1e6).toFixed(2)+'M'):''}</div>
                </td>
                <td style={{padding:'8px 12px'}}>
                  <select value={progs.includes(a.grant_program)?a.grant_program:''} disabled={busy===a.id} onChange={e=>upd(a.id,{grant_program:e.target.value})} style={{padding:'3px 6px',fontSize:12,border:'1px solid #cbd5e1',borderRadius:4}}>
                    {!progs.includes(a.grant_program) && <option value="">{a.grant_program||'(set)'}</option>}
                    {progs.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td style={{padding:'8px 12px'}}>
                  <select value={a.award_status} disabled={busy===a.id} onChange={e=>upd(a.id,{award_status:e.target.value})} style={{padding:'3px 6px',fontSize:12,border:'1px solid #cbd5e1',borderRadius:4,fontWeight:700,color:SC[a.award_status]||'#374151'}}>
                    {STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td style={{padding:'8px 12px',textAlign:'right',color:'#475569'}}>{a.project_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{fontSize:10,color:'#94a3b8',marginTop:8}}>Awards are per-tenant. Defaulted / relinquished awards no longer lock their locations out of other grants.</div>
    </div>
  );
}
